'use client'

import Link from 'next/link'

import RankListItem from './RankListItem'
import RankMoreCta from './RankMoreCta'
import {
  buildModoocarDetailPath,
  RANK_TRACK_CODE,
  useRankImpression,
  useRankTrackingContext,
} from '../_features/rank'

import type { IRankVehicle, TRankTab } from '../_features/rank'

const PREVIEW_ROW_COUNT = 2

interface IRankListProps {
  items: IRankVehicle[]
  previewItems: IRankVehicle[]
  tab: TRankTab
  segment: string
  totalCount: number
  moreButtonText: string
  onMoreClick: () => void
  showChevron?: boolean
  showMoreCta?: boolean
}

const RankList = ({
  items,
  previewItems,
  tab,
  segment,
  totalCount,
  moreButtonText,
  onMoreClick,
  showChevron,
  showMoreCta = true,
}: IRankListProps) => {
  const track = useRankTrackingContext()
  const sectionRef = useRankImpression<HTMLDivElement>(() =>
    track.sectionShow(RANK_TRACK_CODE.RANK_LIST, '차종 랭킹 리스트 노출')
  )

  const backdropItems =
    previewItems.length > 0 ? previewItems : items.slice(-PREVIEW_ROW_COUNT)

  return (
    <div ref={sectionRef} className='flex flex-col lg:gap-dimension-x2'>
      {items.map(item => (
        <Link
          key={item.rank}
          href={buildModoocarDetailPath({
            brandName: item.brandName,
            modelName: item.modelName,
            fuelType: item.fuelType,
            segment,
          })}
          className='block'
          aria-label={`${item.brandName} ${item.modelName} 상세 보기`}
          onClick={() =>
            track.itemClick(
              RANK_TRACK_CODE.RANK_LIST,
              'list_item',
              `${item.brandName} ${item.modelName} 클릭`,
              {
                rank: item.rank,
                target: `${item.brandName} / ${item.modelName}`,
              }
            )
          }
        >
          <RankListItem item={item} tab={tab} showChevron={showChevron} />
        </Link>
      ))}

      {showMoreCta && (
        <div className='relative overflow-hidden'>
          {/* [주의]: 미리보기 행은 배경(absolute)으로 깔고 높이는 CTA가 정합니다. 반대로 하면 CTA가 두 행 높이를 넘어 위아래가 잘립니다. */}
          <div
            aria-hidden
            className='pointer-events-none absolute inset-0 flex flex-col opacity-30 blur-[10px] lg:gap-dimension-x2'
          >
            {backdropItems.map(item => (
              <RankListItem key={item.rank} item={item} tab={tab} />
            ))}
          </div>
          <div className='relative flex items-center justify-center'>
            <RankMoreCta
              totalCount={totalCount}
              buttonText={moreButtonText}
              onMoreClick={onMoreClick}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default RankList
